import * as React from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertTriangle,
  Link2,
  TrendingDown,
  Hash,
  DollarSign,
  Percent,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from 'lucide-react'
import { useTreatmentStats, useUnmatchedItems } from '@/hooks/useTreatments'
import { ManualMappingDialog } from './manual-mapping-dialog'
import type { UnmatchedItem } from '@/lib/api-client'

interface StatCardProps {
  title: string
  value: string | number
  icon: React.ReactNode
  subtitle?: string
  trend?: 'up' | 'down' | 'neutral'
  loading?: boolean
}

function StatCard({ title, value, icon, subtitle, trend, loading }: StatCardProps) {
  if (loading) {
    return (
      <Card className="bg-card">
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-3 w-32" />
            </div>
            <Skeleton className="h-10 w-10 rounded-lg" />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="bg-card">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="mt-1 text-2xl font-bold">{value}</p>
            {subtitle && (
              <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                {trend === 'down' && (
                  <TrendingDown className="h-3 w-3 text-amber-500" />
                )}
                {subtitle}
              </p>
            )}
          </div>
          <div className="rounded-lg bg-muted/50 p-2.5">{icon}</div>
        </div>
      </CardContent>
    </Card>
  )
}

export function UnmatchedTreatmentsTab() {
  const [page, setPage] = React.useState(1)
  const [pageSize] = React.useState(20)
  const [mappingDialogOpen, setMappingDialogOpen] = React.useState(false)
  const [selectedItem, setSelectedItem] = React.useState<UnmatchedItem | null>(null)

  // Fetch stats
  const { data: stats, isPending: statsLoading, refetch: refetchStats } = useTreatmentStats()

  // Fetch unmatched items
  const {
    data: unmatchedData,
    isPending: unmatchedLoading,
    refetch: refetchUnmatched,
  } = useUnmatchedItems({ page, pageSize })

  const unmatched = unmatchedData?.data ?? []
  const totalUnmatched = unmatchedData?.total ?? 0
  const totalPages = Math.ceil(totalUnmatched / pageSize)

  const handleMapClick = (item: UnmatchedItem) => {
    setSelectedItem(item)
    setMappingDialogOpen(true)
  }

  const handleMappingSuccess = () => {
    refetchStats()
    refetchUnmatched()
  }

  const handleRefresh = () => {
    refetchStats()
    refetchUnmatched()
  }

  const matchRate = stats?.matchRate ? (stats.matchRate * 100).toFixed(2) : '0'
  const unmatchedRevenue = unmatched.reduce((sum, item) => sum + item.revenue, 0)

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Match Rate"
          value={`${matchRate}%`}
          icon={<Percent className="h-5 w-5 text-primary" />}
          subtitle={`${stats?.matchedItems?.toLocaleString() ?? 0} of ${stats?.totalItems?.toLocaleString() ?? 0} items`}
          loading={statsLoading}
        />
        <StatCard
          title="Unmatched Items"
          value={stats?.unmatchedItems?.toLocaleString() ?? 0}
          icon={<AlertTriangle className="h-5 w-5 text-amber-500" />}
          subtitle="Invoice line items without treatment"
          trend="down"
          loading={statsLoading}
        />
        <StatCard
          title="Unique Names"
          value={stats?.uniqueUnmatched?.toLocaleString() ?? 0}
          icon={<Hash className="h-5 w-5 text-blue-500" />}
          subtitle="Distinct treatment names to map"
          loading={statsLoading}
        />
        <StatCard
          title="Potential Revenue"
          value={`RM ${unmatchedRevenue.toLocaleString()}`}
          icon={<DollarSign className="h-5 w-5 text-green-500" />}
          subtitle="Revenue from unmatched items"
          loading={unmatchedLoading}
        />
      </div>

      {/* Info Banner */}
      <div className="flex items-center justify-between rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <div>
            <p className="font-medium text-amber-600 dark:text-amber-400">
              {totalUnmatched} unmatched treatment names need attention
            </p>
            <p className="text-sm text-muted-foreground">
              Click "Map" to link each item to a master treatment for accurate reporting
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Unmatched Items Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50%]">Treatment Name</TableHead>
                <TableHead className="text-right">Occurrences</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {unmatchedLoading ? (
                // Loading skeleton
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton className="h-5 w-48" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Skeleton className="ml-auto h-5 w-12" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Skeleton className="ml-auto h-5 w-20" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Skeleton className="ml-auto h-8 w-16" />
                    </TableCell>
                  </TableRow>
                ))
              ) : unmatched.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-32 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="rounded-full bg-green-500/10 p-3">
                        <Percent className="h-6 w-6 text-green-500" />
                      </div>
                      <p className="font-medium text-green-600 dark:text-green-400">
                        All items are matched!
                      </p>
                      <p className="text-sm text-muted-foreground">
                        100% match rate achieved
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                unmatched.map((item, index) => (
                  <TableRow key={item.name} className="group">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Badge
                          variant="outline"
                          className="shrink-0 font-mono text-xs"
                        >
                          #{(page - 1) * pageSize + index + 1}
                        </Badge>
                        <span className="font-mono text-sm">{item.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="secondary">{item.count.toLocaleString()}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      RM {item.revenue.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleMapClick(item)}
                        className="opacity-70 transition-opacity group-hover:opacity-100"
                      >
                        <Link2 className="mr-1.5 h-3.5 w-3.5" />
                        Map
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t px-4 py-3">
              <p className="text-sm text-muted-foreground">
                Showing {(page - 1) * pageSize + 1} to{' '}
                {Math.min(page * pageSize, totalUnmatched)} of {totalUnmatched} items
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Mapping Dialog */}
      <ManualMappingDialog
        open={mappingDialogOpen}
        onOpenChange={setMappingDialogOpen}
        unmatchedItem={selectedItem}
        onSuccess={handleMappingSuccess}
      />
    </div>
  )
}
